// externals
import { useSelector } from 'react-redux';

// components
import type { InterstateTableProps } from '@interstate/components/Table/Types/tableTypes';
import { Button } from '@interstate/components/Button';
import { CheckBox } from '@interstate/components/CheckBox';
import { Table } from '@interstate/components/Table';
import { Interstitial as LoadingIndicator } from '@interstate/components/Interstitial';
import TableRowAlerts from '../vehicleProtectionTableAlerts/TableRowAlerts';
import TableAlertError from '../vehicleProtectionTableAlerts/TableAlertError';
import { tableAlertMessages } from '../vehicleProtectionTableAlerts/constants/tableAlertMessages';
import { TableAlertInputType } from '../../types';
import { ActionWithErrorBadge, dealLockHooks } from '@makemydeal/dr-dash-components';

// libraries
import * as vehicleProtectionSelectors from '@makemydeal/dr-dash-store/selectors/vehicleProtectionSelectors';

// utils
import { getFormattedProviderAddress, isGapProduct, transformProductToModel } from '../../utils/vehicleProtectionTableUtils';
import { useHasContractApiErroredVPP, useHasGapContractError, useHasVppProductError } from '../../utils/useHasVppProductError';

// types
import type { VehicleProtectionTableModel } from '../../types/vehicleProtectionTable';
import type { DealProductWithProductCode, DraftDealProduct } from '@makemydeal/dr-dash-types';

// styles
import {
    ManuallyAddedSign,
    PRODUCT_NAME_CLASSNAME,
    PRODUCT_OVERRIDED_CLASSNAME,
    PRODUCT_PLAN_CLASSNAME,
    PRODUCT_PROVIDER_CLASSNAME,
    RATES_LOADING_SPINNER,
    SELECTED_ROW_CLASSNAME,
    VehicleProtectionTableActions,
    VehicleProtectionTableContainer,
    VppTableCheckBoxContainer
} from './VehicleProtectionTable.style';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { DEFAULT_PAGE_SIZE } from '../vehicleProtection/constants';
import { VehicleProtectionTableCell } from './VehicleProtectionTableCell';
import { ManualVppTableCell } from '../manualVppTable/ManualVppTableCell';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { CatalogTableColumns } from '../../constants';
import { useVehicleProtectionContext } from '../../context/VehicleProtectionContext';

type VehicleProtectionTableProps = {
    products: DraftDealProduct[];
    onProductApply: (record: DealProductWithProductCode) => void;
    onProductApplyAll: () => void;
    onProductEdit: (record: DealProductWithProductCode) => void;
    currentPage: number;
    setCurrentPage: (page: number) => void;
    id?: string;
};

const codeColumnPercent = '4.6%';
const typeColumnPercent = '9.4%';
const profitColumnPercent = '7%';
const productNameColumnPercent = '14.6%';
const milesColumnPercent = '6.8%';
const priceColumnPercent = '6.4%';
const providerColumnPercent = '8.6%';

const VehicleProtectionTableCompact = ({
    products,
    onProductApply,
    onProductApplyAll,
    onProductEdit,
    currentPage,
    setCurrentPage,
    id
}: VehicleProtectionTableProps) => {
    const isFetchingRates = useSelector(vehicleProtectionSelectors.isFetchingVppRates);
    const isFetchingProducts = useSelector(vehicleProtectionSelectors.isFetchingVppProducts);
    const isFetchingFIE = isFetchingProducts || isFetchingRates;
    const productAttributes = useSelector(vehicleProtectionSelectors.getVppProductAttributes);
    const isGCDynamicValidationsEnabled = useSelector(featureToggleSelectors.isGCDynamicValidationsEnabled);
    const isMVVppOverrideIndicatorEnabled = useSelector(featureToggleSelectors.isMVVppOverrideIndicatorEnabled);

    const theme = useInterstateTheme();
    const isLocked = dealLockHooks.useIsDealLocked();
    const { isDarwinVPPEditDisabled } = useVehicleProtectionContext();
    const models = products.filter(Boolean).map((product) => transformProductToModel(product, isFetchingRates, productAttributes));
    const productsEligibleForSelection = useSelector(vehicleProtectionSelectors.getVppProductsEligibleForSelection);
    const hasProductError = useHasVppProductError();
    const hasGapContractError = useHasGapContractError();
    const hasContractApiError = useHasContractApiErroredVPP();

    const columns: InterstateTableProps['columns'] = [
        {
            title: (
                <VppTableCheckBoxContainer>
                    <CheckBox
                        onChange={onProductApplyAll}
                        value="All"
                        data-testid={`select-all-product-checkbox-id`}
                        checked={
                            productsEligibleForSelection.length > 0 &&
                            productsEligibleForSelection.every((product) => product.selected)
                        }
                        disabled={products.length === 0 || isLocked || isDarwinVPPEditDisabled || isFetchingFIE}
                    />
                </VppTableCheckBoxContainer>
            ),
            width: '4rem',
            align: 'right',
            fixed: 'left',
            onCell: (record: VehicleProtectionTableModel) => {
                const productCode = record.product.productCode;
                const isDivergedFromDarwinBaseline =
                    productCode &&
                    !record.isManualProduct &&
                    record.selected &&
                    productAttributes[productCode]?.isDivergedFromDarwinBaseline;
                return {
                    className: isMVVppOverrideIndicatorEnabled && isDivergedFromDarwinBaseline ? PRODUCT_OVERRIDED_CLASSNAME : ''
                };
            },
            render: (keyValue, record: VehicleProtectionTableModel, index) => {
                if (record.isManualProduct) {
                    return <ManualVppTableCell name={CatalogTableColumns.SELECT_PRODUCT} draftProduct={record.product} />;
                }

                const product = record.product as DealProductWithProductCode;
                const productCode = product.productCode;
                const unselectDisabled = !productCode || (record.selected && productAttributes[productCode]?.unselectDisabled);
                const selectDisabled = !productCode || (!record.selected && productAttributes[productCode]?.selectDisabled);

                return (
                    <VppTableCheckBoxContainer>
                        <CheckBox
                            data-testid={`${record.selected ? 'selected' : 'unselected'}-product-checkbox-id`}
                            onChange={() => onProductApply(product)}
                            value={String(index)}
                            checked={record.selected}
                            disabled={selectDisabled || unselectDisabled || isLocked || isDarwinVPPEditDisabled}
                        />
                    </VppTableCheckBoxContainer>
                );
            }
        },
        {
            title: '',
            width: '3rem',
            fixed: 'left',
            render: (keyValue, record) => {
                if (record.isManualProduct) return null;
                const productCode = record.product.productCode;
                if (isFetchingRates) {
                    return (
                        <div className={RATES_LOADING_SPINNER} data-testid="rates-loading-spinner">
                            <LoadingIndicator size="small" />
                        </div>
                    );
                }
                const isGAP = isGapProduct(record.product);
                const showContractValidationAlert =
                    record.selected && ((isGAP && hasGapContractError(record.product)) || hasContractApiError(record.product));
                if (showContractValidationAlert) {
                    return (
                        <TableAlertError
                            tableAlertInput={{
                                type: TableAlertInputType.ERROR,
                                message: tableAlertMessages.contractValidationError
                            }}
                        />
                    );
                }
                return productCode && <TableRowAlerts productAttributes={productAttributes[productCode]}></TableRowAlerts>;
            }
        },
        {
            title: 'Type',
            dataIndex: CatalogTableColumns.PRODUCT_CATEGORY_CODE,
            width: typeColumnPercent,
            fixed: 'left',
            render: (keyValue, record: VehicleProtectionTableModel) => {
                if (record.isManualProduct) {
                    return (
                        <ManualVppTableCell
                            name={CatalogTableColumns.PRODUCT_CATEGORY_CODE}
                            draftProduct={record.product}
                            displayValue={
                                <>
                                    <VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />
                                    <ManuallyAddedSign>Manually Added</ManuallyAddedSign>
                                </>
                            }
                        />
                    );
                }
                return <VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />;
            }
        },
        {
            title: 'Product Name',
            dataIndex: CatalogTableColumns.PRODUCT_NAME,
            width: productNameColumnPercent,
            className: PRODUCT_NAME_CLASSNAME,
            render: (keyValue, record) => {
                if (record.isManualProduct) {
                    return (
                        <ManualVppTableCell
                            name={CatalogTableColumns.PRODUCT_NAME}
                            draftProduct={record.product}
                            displayValue={<VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />}
                        />
                    );
                }

                return <VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />;
            }
        },
        {
            title: 'Provider',
            dataIndex: CatalogTableColumns.PRODUCT_PROVIDER_NAME,
            width: providerColumnPercent,
            className: PRODUCT_PROVIDER_CLASSNAME,
            render: (productProviderName, record: VehicleProtectionTableModel) => {
                const shouldRenderProviderAddress = isGapProduct(record);
                const tooltipContent = shouldRenderProviderAddress ? getFormattedProviderAddress(record) : productProviderName;

                if (record.isManualProduct) {
                    return (
                        <ManualVppTableCell
                            name={CatalogTableColumns.PRODUCT_PROVIDER_NAME}
                            draftProduct={record.product}
                            displayValue={
                                <VehicleProtectionTableCell value={productProviderName} tooltipContent={tooltipContent} />
                            }
                        />
                    );
                }

                return <VehicleProtectionTableCell value={productProviderName} tooltipContent={tooltipContent} />;
            }
        },
        {
            title: 'Plan/Coverage',
            dataIndex: CatalogTableColumns.PRODUCT_PROVIDER_PLAN,
            className: PRODUCT_PLAN_CLASSNAME,
            render: (keyValue, record) => {
                if (record.isManualProduct) {
                    return (
                        <ManualVppTableCell
                            name={CatalogTableColumns.PRODUCT_PROVIDER_PLAN}
                            draftProduct={record.product}
                            displayValue={<VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />}
                        />
                    );
                }
                return <VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />;
            }
        },
        {
            title: 'Months',
            dataIndex: CatalogTableColumns.PRODUCT_COVERAGE_LENGTH,
            width: codeColumnPercent,
            render: (keyValue, record) => {
                if (record.isManualProduct) {
                    return (
                        <ManualVppTableCell
                            name={CatalogTableColumns.PRODUCT_COVERAGE_LENGTH}
                            draftProduct={record.product}
                            displayValue={keyValue}
                        />
                    );
                }

                return keyValue;
            }
        },
        {
            title: 'Miles',
            dataIndex: CatalogTableColumns.PRODUCT_COVERAGE_MILES,
            width: milesColumnPercent,
            render: (keyValue, record) => {
                if (record.isManualProduct) {
                    return (
                        <ManualVppTableCell
                            name={CatalogTableColumns.PRODUCT_COVERAGE_MILES}
                            draftProduct={record.product}
                            displayValue={<VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />}
                        />
                    );
                }

                return <VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />;
            }
        },
        {
            title: 'Code',
            dataIndex: CatalogTableColumns.PRODUCT_CODE,
            width: codeColumnPercent,
            render: (keyValue, record) => {
                if (record.isManualProduct) {
                    return (
                        <ManualVppTableCell
                            name={CatalogTableColumns.PRODUCT_CODE}
                            draftProduct={record.product}
                            displayValue={keyValue}
                        />
                    );
                }

                return keyValue;
            }
        },
        {
            title: 'Profit',
            dataIndex: CatalogTableColumns.PROFIT_LOCATION,
            width: profitColumnPercent,
            render: (keyValue, record) => {
                if (record.isManualProduct) {
                    return (
                        <ManualVppTableCell
                            name={CatalogTableColumns.PROFIT_LOCATION}
                            draftProduct={record.product}
                            displayValue={keyValue}
                        />
                    );
                }

                return keyValue;
            }
        },
        {
            title: 'Deductible',
            dataIndex: CatalogTableColumns.PRODUCT_DEDUCTIBLE,
            width: milesColumnPercent,
            render: (keyValue, record) => {
                if (record.isManualProduct) {
                    return (
                        <ManualVppTableCell
                            name={CatalogTableColumns.PRODUCT_DEDUCTIBLE}
                            draftProduct={record.product}
                            displayValue={<VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />}
                        />
                    );
                }
                return <VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />;
            }
        },
        {
            title: 'Cost',
            dataIndex: 'productCost',
            width: priceColumnPercent,
            render: (keyValue, record) => {
                if (record.isManualProduct) {
                    return (
                        <ManualVppTableCell
                            name={CatalogTableColumns.PRODUCT_COST}
                            draftProduct={record.product}
                            displayValue={<VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />}
                        />
                    );
                }

                return <VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />;
            }
        },
        {
            title: 'Markup',
            dataIndex: CatalogTableColumns.MARKUP_VALUE,
            width: priceColumnPercent,
            render: (keyValue, record) => {
                if (record.isManualProduct) {
                    return (
                        <ManualVppTableCell
                            name={CatalogTableColumns.MARKUP_VALUE}
                            draftProduct={record.product}
                            displayValue={<VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />}
                        />
                    );
                }

                return <VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />;
            }
        },
        {
            title: 'Price',
            dataIndex: CatalogTableColumns.PRODUCT_PRICE,
            width: priceColumnPercent,
            render: (keyValue, record) => {
                if (record.isManualProduct) {
                    return (
                        <ManualVppTableCell
                            name={CatalogTableColumns.PRODUCT_PRICE}
                            draftProduct={record.product}
                            displayValue={<VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />}
                        />
                    );
                }

                return <VehicleProtectionTableCell value={keyValue} tooltipContent={keyValue} />;
            }
        },
        {
            title: 'Actions',
            align: 'center',
            width: codeColumnPercent,
            fixed: 'right',
            render: (keyValue, record: VehicleProtectionTableModel) => {
                if (record.isManualProduct) {
                    return <ManualVppTableCell name={CatalogTableColumns.ACTIONS} draftProduct={record.product} />;
                }

                const product = record.product as DealProductWithProductCode;
                const productCode = product.productCode;
                const editDisabled = !productCode || productAttributes[productCode]?.editingDisabled;

                return (
                    <VehicleProtectionTableActions>
                        <Button
                            buttonStyle={'aux'}
                            size={'small'}
                            data-testid="vpp-edit-button-action"
                            disabled={isLocked || editDisabled || isDarwinVPPEditDisabled}
                            onClick={() => onProductEdit(product)}
                        >
                            {isGCDynamicValidationsEnabled ? (
                                <ActionWithErrorBadge
                                    entityName={DealTabs.PROTECTION}
                                    hasError={hasProductError(product)}
                                    xOffset="-0.2rem"
                                    yOffset="-0.15rem"
                                >
                                    Edit
                                </ActionWithErrorBadge>
                            ) : (
                                'Edit'
                            )}
                        </Button>
                    </VehicleProtectionTableActions>
                );
            }
        }
    ];

    return (
        <VehicleProtectionTableContainer theme={theme} $readOnly={isDarwinVPPEditDisabled}>
            <Table
                data-testid="vehicle-protection-table-id"
                id={id}
                columns={columns}
                data={models}
                highlightOnHover={false}
                emptyText="No products to display"
                enablePagination={true}
                paginationCurrent={currentPage}
                onChangePage={setCurrentPage}
                dataDensity="small"
                rowClassName={(record: VehicleProtectionTableModel) => (record.selected ? SELECTED_ROW_CLASSNAME : '')}
                scrollX={1350}
                defaultPageSize={DEFAULT_PAGE_SIZE}
            />
        </VehicleProtectionTableContainer>
    );
};

export default VehicleProtectionTableCompact;
